import { useEffect, useRef } from 'react';
import { cn } from '@ai-tutor/ui';
import { useIsDark } from '@/hooks/ui/useIsDark';
import type { FocusPhase } from '@/lib/focus/focusMachine';
import type { SakuraProgress } from '../focusViewModel';
import { sakuraPoster, sakuraStage } from './sakuraStage';

interface SakuraSceneProps {
  progress: SakuraProgress;
  phase: FocusPhase;
  /**
   * Whether this slot holds the live scene. The page's box gives it up while
   * focus mode is open, so the one canvas moves to the full screen.
   */
  active?: boolean;
  className?: string;
}

/**
 * The painted sakura, alive: a slot the shared scene canvas is moved into.
 * Until the canvas has drawn - or where WebGL is missing - the still poster
 * shows in its place, so there is never an empty box.
 */
export function SakuraScene({
  progress,
  phase,
  active = true,
  className,
}: SakuraSceneProps) {
  const slot = useRef<HTMLDivElement>(null);
  const night = useIsDark();

  useEffect(() => {
    const el = slot.current;
    if (!active || !el) return;
    void sakuraStage.attach(el, night);
    return () => sakuraStage.detach(el);
  }, [active, night]);

  useEffect(() => {
    if (active) sakuraStage.setState({ ...progress, phase });
  }, [active, progress.bloom, progress.fall, progress.falling, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={slot}
      aria-hidden='true'
      className={cn('absolute inset-0 bg-cover bg-[60%_50%]', className)}
      style={{ backgroundImage: `url(${sakuraPoster(night)})` }}
    />
  );
}
